import { REG_PATH_KEYS, REG_PATH_STEPS, PAGES, AOFL_PRODUCT_NAMES } from '$lib/utils/constants';
import * as Sentry from '@sentry/browser';
import { isAbcmouseUserIn2_0 } from '$lib/utils/cross-auth';
import { AnalyticsService } from '../analytics';

const REGISTRATION_PATH_MAP = {
	[AOFL_PRODUCT_NAMES.ABCMOUSE]: {
		[REG_PATH_KEYS.DEFAULT_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.DEFAULT_RESUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.AA_OFFER]: PAGES.INTERNAL.AA_OFFER,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		// Adding this path for historical support for migrated users.
		[REG_PATH_KEYS.NO_AA_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.IN_APP_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.ASSESSMENT]: PAGES.INTERNAL.ASSESSMENT_PURCHASE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.IN_APP_RESUBSCRIBE]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.GOOGLE_IN_APP_SUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.IN_APP_UCB_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.IN_APP_UCB_RESUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.SUBSCRIBER_NO_PATH]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.RESUBSCRIBE_NO_PATH]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.IN_APP_SAMSUNG_SUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		},
		[REG_PATH_KEYS.IN_APP_SAMSUNG_RESUBSCRIBER]: {
			[REG_PATH_STEPS.COMPLETE]: PAGES.INTERNAL.INSIDE_PAYWALL
		}
	},
	[AOFL_PRODUCT_NAMES.ABCMOUSE_2_0]: {
		[REG_PATH_KEYS.DEFAULT_SUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP
		},
		[REG_PATH_KEYS.DEFAULT_RESUBSCRIBER]: {
			[REG_PATH_STEPS.UPGRADE]: PAGES.INTERNAL.UPGRADE,
			[REG_PATH_STEPS.DOWNLOAD_APP]: PAGES.INTERNAL.DOWNLOAD_APP
		}
	}
};

/**
 *
 */
class RegistrationPathService {
	/**
	 * Retrieves the next registration path for the user.
	 * @param {import('$lib/stores/user/store').UserStoreState} user - The user object.
	 * @returns {string} A promise that resolves to the resolved condition.
	 */
	static next(user) {
		return RegistrationPathService.getRedirectPath(user);
	}

	/**
	 * Returns the next step in the registration path for a given user.
	 * @param {import('$lib/stores/user/store').UserStoreState} user - The user object.
	 * @returns {string} - The next step in the registration path.
	 */
	static getNextStep(user) {
		const { subscription_path, completed_step } = user.userMetaData;
		const { roles } = user;

		let steps = REGISTRATION_PATH_MAP[AOFL_PRODUCT_NAMES.ABCMOUSE][subscription_path] || {};
		if (isAbcmouseUserIn2_0(roles)) {
			steps = REGISTRATION_PATH_MAP[AOFL_PRODUCT_NAMES.ABCMOUSE_2_0][subscription_path] || {};
		}
		const stepKeys = Object.keys(steps);
		const currentIndex = stepKeys.indexOf(completed_step);
		const nextIndex = currentIndex + 1;

		if (!completed_step || !stepKeys.includes(completed_step)) {
			return stepKeys[0];
		}

		return stepKeys[nextIndex];
	}

	/**
	 * Retrieves the redirect condition based on the user's information.
	 * @param {import('$lib/stores/user/store').UserStoreState} user - The user object.
	 * @returns {string} - The redirect condition object.
	 */
	static getRedirectPath(user) {
		let nextStep;
		let nextPage;

		try {
			const { roles } = user;
			nextStep = this.getNextStep(user);

			let nextPage = REGISTRATION_PATH_MAP[AOFL_PRODUCT_NAMES.ABCMOUSE][user?.userMetaData?.subscription_path]?.[nextStep];
			if (isAbcmouseUserIn2_0(roles)) {
				nextPage = REGISTRATION_PATH_MAP[AOFL_PRODUCT_NAMES.ABCMOUSE_2_0][user?.userMetaData?.subscription_path]?.[nextStep];
			}

			switch (user?.accountStatus) {
				case 'active':
					if (!nextPage) {
						issueException('Error: nextPage is not available for this user', false, user);
						return PAGES.INTERNAL.INSIDE_PAYWALL;
					} else return nextPage;
				case 'auxiliary_only_active':
					if (!nextPage) {
						issueException('Error: nextPage is not available for this user', false, user);
						return PAGES.INTERNAL.INSIDE_PAYWALL;
					} else return nextPage;
				case 'inactive':
					return PAGES.INTERNAL.REACTIVATION;
				case 'none':
					if (user.tokenValid)
						issueException('Error: "valid" token user with "none" account status', true, user);
					return PAGES.INTERNAL.HOME;
				default:
					return PAGES.INTERNAL.HOME;
			}
		} catch (e) {
			Sentry.captureException(e, {
				extra: {
					nextStep,
					nextPage,
					action: 'Error: Registration Path Service'
				}
			});
			return '/';
		}
	}
}

/**
 *
 * @param {string} message - the error message to display
 * @param {boolean} isExternal - whether to issue this exception externally
 * @param {object} user - the user object when the error happen
 */
function issueException(message, isExternal, user) {
	if (isExternal) {
		Sentry.captureException(message, {
			extra: { user }
		});
	} else {
		AnalyticsService.trackClientDebugMetric('reg-path:next-path-exception', {
			data: { user, message }
		});
	}
}

export { RegistrationPathService };
